import { Text, Box, Tag, TagLabel, Wrap, WrapItem, Button } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import questionnaireService from "api/issb/questionnaire.service";
import { useAuth } from "context";
import { typedId, baseIdFromTypedId } from "api/issb/reportdoc.service";
import PageHeader from "components/text/PageHeader";
import Issb from "api/issb/issb";
import { isFoundationLikeRound } from "utils/roundFilters";

const QUESTION_TYPE_PREFIX = "Question:";
const isQuestionTypedId = (tid) => tid?.startsWith(QUESTION_TYPE_PREFIX);

const STAGE_OPTIONS = [
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "INTERNAL_REVIEW", label: "Internal Review" },
  { value: "AUDIT", label: "Audit" },
  { value: "COMPLETE", label: "Complete" },
];

function buildStageFilter(stages) {
  if (!stages?.length || stages.length === STAGE_OPTIONS.length) return "*";
  return stages.map((s) => `stageType:${s}`).join(" OR ");
}

export default function ReportDocSourcesPanel({
  setNextDisabled,
  sourceTypedIds,
  setSourceTypedIds,
  questionFilter,
  setQuestionFilter,
}) {
  const roundSelector = useSelector((state) => state.issbRound);
  const selectedRound = roundSelector?.selectedRound;
  const { getAccessTokenSilently } = useAuth();
  const { data: questionnairePage } = useQuery(["questionnaireService.list", "all"], async () => {
    await getAccessTokenSilently();
    return questionnaireService.list({ sort: "createdDate", direction: "desc" });
  });

  const nonFoundationRounds = questionnairePage?.content?.filter((r) => !isFoundationLikeRound(r));

  // Round-only typed IDs (exclude foundation question typed IDs)
  const roundTypedIds = useMemo(
    () => (sourceTypedIds || []).filter((tid) => !isQuestionTypedId(tid)),
    [sourceTypedIds]
  );

  const selectedRoundIds = useMemo(() => roundTypedIds.map(baseIdFromTypedId), [roundTypedIds]);

  // Pre-select current round on first load
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return;
    if (!selectedRound || isFoundationLikeRound(selectedRound)) return;
    if (!nonFoundationRounds) return;
    initRef.current = true;
    const currentTid = typedId("IssbQuestionnaire", selectedRound.id);
    setSourceTypedIds((prev) => {
      if (prev.includes(currentTid)) return prev;
      return [...prev, currentTid];
    });
  }, [selectedRound, nonFoundationRounds, setSourceTypedIds]);

  // Stage filtering
  const [selectedStages, setSelectedStages] = useState(() => STAGE_OPTIONS.map((s) => s.value));

  // Fetch questions for selected rounds to determine which stages are present
  const roundIdsKey = selectedRoundIds?.join(",") || "";
  const { data: roundQuestions } = useQuery(
    ["roundQuestions", roundIdsKey],
    async () => {
      await getAccessTokenSilently();
      const results = await Promise.all(selectedRoundIds.map((id) => Issb.getQuestionsOfRound({ id, paramsString: "" })));
      return results.flat();
    },
    { enabled: selectedRoundIds?.length > 0 }
  );

  const presentStages = useMemo(() => {
    if (!roundQuestions) return new Set();
    return new Set(roundQuestions.map((q) => q.stageType));
  }, [roundQuestions]);

  // Check if any of the four standard stages are present
  const hasAnyStage = STAGE_OPTIONS.some((s) => presentStages.has(s.value));

  const handleToggleStage = (stageValue) => {
    setSelectedStages((prev) =>
      prev.includes(stageValue) ? prev.filter((s) => s !== stageValue) : [...prev, stageValue]
    );
  };

  // Sync questionFilter when stages change — if no standard stages exist, always send "*"
  useEffect(() => {
    setQuestionFilter(hasAnyStage ? buildStageFilter(selectedStages) : "*");
  }, [selectedStages, hasAnyStage, setQuestionFilter]);

  const handleToggleRound = (roundId) => {
    const tid = typedId("IssbQuestionnaire", roundId);
    const questionTids = (sourceTypedIds || []).filter(isQuestionTypedId);
    let newRoundTids;
    if (roundTypedIds.includes(tid)) {
      newRoundTids = roundTypedIds.filter((t) => t !== tid);
    } else {
      newRoundTids = [...roundTypedIds, tid];
    }
    setSourceTypedIds([...questionTids, ...newRoundTids]);
  };

  const handleSelectAll = () => {
    const questionTids = (sourceTypedIds || []).filter(isQuestionTypedId);
    const allRoundTids = nonFoundationRounds?.map((q) => typedId("IssbQuestionnaire", q.id)) || [];
    const isAllSelected = selectedRoundIds.length === nonFoundationRounds?.length;
    setSourceTypedIds(isAllSelected ? [...questionTids] : [...questionTids, ...allRoundTids]);
  };

  // Validation: must have at least one selection
  useEffect(() => {
    setNextDisabled(sourceTypedIds?.length === 0);
  }, [sourceTypedIds, setNextDisabled]);

  return (
    <Box>
      <PageHeader
        superTitle="Report Generator"
        title="Select Disclosure Rounds"
        subTitle="Select the data and rounds to reference when compiling your report."
      />
      <Box flexGrow={1} mt={4}>
        {/* No Foundation Data card: v1's (FoundationSourceSelector) is hidden since ESG-4024, and the report
            generator has no v2 source yet. A report doc saved with v1 question ids keeps them — the
            Question: typed ids are still carried through untouched below. */}
        {/* Rounds */}
        <Box mb={3} py={4} px={6} borderWidth="1px" borderColor="lime" borderRadius="xl" bg="white">
          <Text fontSize="body.m" fontWeight="bold">
            Rounds
            {selectedRoundIds?.length > 0 && (
              <Text as="span" fontWeight="normal" color="gray.500">
                {" "}
                ({selectedRoundIds.length})
              </Text>
            )}
          </Text>
          <Text fontSize="sm" color="gray.500">
            Reference answers from specific rounds and frameworks
          </Text>
          <Box mt={3}>
            <Wrap spacing={2} align="center">
              {nonFoundationRounds
                ?.slice()
                .sort((a, b) => {
                  if (a.id === selectedRound?.id) return -1;
                  if (b.id === selectedRound?.id) return 1;
                  return 0;
                })
                .map((round) => {
                  const isSelected = selectedRoundIds.includes(round.id);
                  const isCurrent = round.id === selectedRound?.id;
                  return (
                    <WrapItem key={round.id}>
                      <Tag
                        size="sm"
                        borderRadius="full"
                        bg={isSelected ? "lightLime" : "gray.100"}
                        border="1px solid"
                        borderColor={isSelected ? "lime" : "gray.300"}
                        color={isSelected ? "inherit" : "gray.400"}
                        px={3}
                        py={1}
                        cursor="pointer"
                        onClick={() => handleToggleRound(round.id)}
                      >
                        <TagLabel noOfLines={1}>{isCurrent ? `${round.name} (current)` : round.name}</TagLabel>
                        <Box as="span" ml={1} fontSize="xs">
                          {isSelected ? "\u2715" : "+"}
                        </Box>
                      </Tag>
                    </WrapItem>
                  );
                })}
            </Wrap>
            {nonFoundationRounds?.length > 5 && (
              <Box display="flex" gap={3} mt={3}>
                <Button variant="link" size="xs" color="gray.500" fontWeight="normal" onClick={handleSelectAll}>
                  Select all
                </Button>
                <Button
                  variant="link"
                  size="xs"
                  color="gray.500"
                  fontWeight="normal"
                  onClick={() => {
                    const questionTids = (sourceTypedIds || []).filter(isQuestionTypedId);
                    setSourceTypedIds([...questionTids]);
                  }}
                >
                  Deselect all
                </Button>
              </Box>
            )}
          </Box>

          {/* In status filter — only shown when selected rounds have questions with recognised stages */}
          {selectedRoundIds?.length > 0 && hasAnyStage && (
            <Box mt={4}>
              <Text fontSize="sm" fontWeight="600" color="gray.600" mb={2}>
                In status
              </Text>
              <Wrap spacing={2}>
                {STAGE_OPTIONS.map((stage) => {
                  const hasQuestions = presentStages.has(stage.value);
                  const isSelected = selectedStages.includes(stage.value);
                  return (
                    <WrapItem key={stage.value}>
                      <Tag
                        size="sm"
                        borderRadius="full"
                        bg={isSelected && hasQuestions ? "lightLime" : "gray.100"}
                        border="1px solid"
                        borderColor={isSelected && hasQuestions ? "lime" : "gray.300"}
                        color={isSelected && hasQuestions ? "inherit" : "gray.400"}
                        px={3}
                        py={1}
                        cursor={hasQuestions ? "pointer" : "not-allowed"}
                        opacity={hasQuestions ? 1 : 0.5}
                        onClick={() => hasQuestions && handleToggleStage(stage.value)}
                      >
                        <TagLabel>{stage.label}</TagLabel>
                        <Box as="span" ml={1} fontSize="xs">
                          {isSelected && hasQuestions ? "\u2715" : "+"}
                        </Box>
                      </Tag>
                    </WrapItem>
                  );
                })}
              </Wrap>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
